import { Navigate } from "react-router";
import { listWorkspaceMembers, listWorkspaces } from "@/lib/api/endpoints";
import type {
  PendingWorkspaceInvitation,
  WorkspaceMember,
  WorkspaceRole,
} from "@/lib/api/types";
import { useCurrentUser } from "@/lib/auth/session";
import { readActiveCustomerFromDocument } from "@/lib/mapper/active-customer-cookie";
import { PageError, PageLoading, useLoader } from "@/lib/loader";
import { MembersTable } from "./MembersTable";

export function MembersPage() {
  const user = useCurrentUser();
  const loaded = useLoader(async () => {
    const workspaces = await listWorkspaces();
    if (workspaces.length === 0) return null;
    const activeId = readActiveCustomerFromDocument();
    const active = (activeId && workspaces.find((w) => w.id === activeId)) || workspaces[0];
    let members: WorkspaceMember[] = [];
    let invitations: PendingWorkspaceInvitation[] = [];
    let loadError: string | null = null;
    try {
      const res = await listWorkspaceMembers(active.id);
      members = res.members;
      invitations = res.invitations;
    } catch {
      loadError = "Could not load members for this workspace.";
    }
    return { active, members, invitations, loadError };
  }, []);

  if (loaded.status === "error") return <PageError error={loaded.error} />;
  if (loaded.data === undefined) return <PageLoading />;
  // No workspaces at all → nothing to manage; send to the dashboard.
  if (loaded.data === null) return <Navigate to="/" replace />;
  const { active, members, invitations, loadError } = loaded.data;

  // Caller's role in the ACTIVE workspace is the source of truth for gating.
  const myRole: WorkspaceRole = active.role;
  const canManage =
    myRole === "OWNER" ||
    myRole === "ADMIN" ||
    user.role === "SUPER_ADMIN";

  const currentEmail = (user.email ?? "").toLowerCase();

  return (
    <div className="mx-auto max-w-5xl px-5 py-8 lg:px-6">
      <div className="mb-8">
        <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-500 dark:text-zinc-400">
          Workspace
        </p>
        <h1 className="font-sans text-2xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-100">
          Members
        </h1>
        <p className="mt-1 text-[13px] text-zinc-600 dark:text-zinc-400">
          People with access to{" "}
          <span className="font-medium text-zinc-800 dark:text-zinc-200">
            {active.name}
          </span>
          {canManage
            ? ". Invite teammates by email and manage their roles."
            : ". Ask an owner or admin to invite teammates."}
        </p>
      </div>

      {loadError ? (
        <div className="rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300">
          {loadError}
        </div>
      ) : (
        <MembersTable
          members={members}
          invitations={invitations}
          canManage={canManage}
          currentEmail={currentEmail}
          myRole={myRole}
        />
      )}
    </div>
  );
}
